import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AutoTagCondition,
  AutoTagConditionCreateRequest,
  AutoTagConditionDeleteRequest,
  AutoTagConditionUpdateRequest,
  Question,
  QuestionType,
  RequestOptions,
  SnackbarState,
  Tag,
  TransformedAutoTagCondition,
  UseAddAutoTagConditionResult,
  UseAutoTagConditionsResult,
  UseCreateTagResult,
  UseDeleteAutoTagConditionResult,
  UseUpdateAutoTagConditionResult
} from './type';

// Constants
export const SUPPORTED_QUESTION_TYPES: QuestionType[] = [
  'RadioButtonField',
  'SelectField',
  'CheckBoxField'
];

// Helper functions
export const createRequestOptions = (method: string, body: any = null): RequestOptions => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  ...(body && { body: JSON.stringify(body) })
});

export const deepCopy = (obj: any) => JSON.parse(JSON.stringify(obj));

/**
 * Transform condition data to include fields array
 * This function converts the flat condition data from the API into a structured format for the UI
 * It supports multiple field choices for different question types (especially CheckBoxField)
 */
export const transformCondition = (condition: AutoTagCondition): TransformedAutoTagCondition => {
  // Create a field object for the main field
  // Using choices array instead of a single choice to support multiple selections for CheckBoxField
  const mainField = {
    id: condition.id,
    question: { id: condition.field_id, title: condition.field?.title || '' },
    choices: condition.field_choices || []
  };

  return {
    ...condition,
    tags: condition.tags || [],
    fields: [mainField]
  };
};

// Data fetching hooks
export const useSupportedQuestions = (jobId: number) => {
  return useQuery<Question[], Error>({
    queryKey: ['radioQuestions', jobId],
    queryFn: async () => {
      const response = await fetch(`/api/v4/fields?job_id=${jobId}`, { method: 'GET' }).then(
        (response) => response.json()
      );
      return response.fields.filter((question: Question) =>
        SUPPORTED_QUESTION_TYPES.includes(question.type as QuestionType)
      );
    }
  });
};

export const useTags = () => {
  return useQuery<Tag[], Error>({
    queryKey: ['tags'],
    queryFn: async () => {
      const response = await fetch(`/api/v4/tags?simple=true`, { method: 'GET' }).then((response) =>
        response.json()
      );
      return response.tags || [];
    }
  });
};

export const useAutoTagConditions = (
  jobId: number,
  setAutoTagConditions: (conditions: TransformedAutoTagCondition[]) => void,
  setSnackbar: (snackbar: SnackbarState) => void
): UseAutoTagConditionsResult => {
  const fetchAutoTagConditions = useCallback(async () => {
    try {
      const response = await fetch(`/api/v4/jobs/${jobId}/auto_tag_conditions`, {
        method: 'GET'
      }).then((response) => response.json());
      setAutoTagConditions((response || []).map(transformCondition));
    } catch (error) {
      console.log(error);
      setSnackbar({ message: 'Error fetching auto tag conditions', state: 'error' });
    }
  }, [jobId, setSnackbar, setAutoTagConditions]);

  return { fetchAutoTagConditions };
};

// Generic error handler for mutations
export const handleMutationError = (
  error: Error,
  defaultMessage: string,
  setSnackbar: (snackbar: SnackbarState) => void
) => setSnackbar({ message: error.message || defaultMessage, state: 'error' });

// Mutation hooks
export const useAddAutoTagCondition = (
  jobId: number,
  supportedQuestions: Question[],
  tags: Tag[],
  autoTagConditions: TransformedAutoTagCondition[],
  setAutoTagConditions: (conditions: TransformedAutoTagCondition[]) => void,
  setSnackbar: (snackbar: SnackbarState) => void
): UseAddAutoTagConditionResult => {
  return useMutation<AutoTagCondition, Error, void, unknown>({
    mutationFn: async () => {
      // Get the first question and its field choices
      const firstQuestion = supportedQuestions[0];
      // For CheckBoxField, we can select multiple choices
      const isCheckbox = firstQuestion?.type === 'CheckBoxField';
      // For CheckBoxField, use all choices, for other types use just the first choice
      const fieldChoiceIds = isCheckbox
        ? firstQuestion?.field_choices.map((choice) => choice.id)
        : [firstQuestion?.field_choices[0]?.id];

      const response = await fetch(
        `/api/v4/jobs/${jobId}/auto_tag_conditions`,
        createRequestOptions('POST', {
          auto_tag_condition: {
            field_id: firstQuestion?.id,
            field_choice_ids: fieldChoiceIds,
            tag_ids: [tags[0]?.id]
          } as AutoTagConditionCreateRequest
        })
      );
      if (!response.ok) throw new Error('Error adding auto tag condition');
      return response.json();
    },
    onSuccess: (data) => {
      setAutoTagConditions([...autoTagConditions, transformCondition(data)]);
    },
    onError: (error: Error) =>
      handleMutationError(error, 'Error creating auto tag condition', setSnackbar)
  });
};

export const useDeleteAutoTagCondition = (
  jobId: number,
  autoTagConditions: TransformedAutoTagCondition[],
  setAutoTagConditions: (conditions: TransformedAutoTagCondition[]) => void,
  setSnackbar: (snackbar: SnackbarState) => void
): UseDeleteAutoTagConditionResult => {
  return useMutation<
    void,
    Error,
    AutoTagConditionDeleteRequest,
    { previousAutoTagConditions: TransformedAutoTagCondition[] }
  >({
    mutationFn: async ({ id }: AutoTagConditionDeleteRequest) => {
      const response = await fetch(
        `/api/v4/jobs/${jobId}/auto_tag_conditions/${id}`,
        createRequestOptions('DELETE')
      );
      if (!response.ok) throw new Error('Error deleting auto tag condition');
    },
    onMutate: async ({ index }) => {
      const autoTagConditionsCopy = deepCopy(autoTagConditions);
      autoTagConditionsCopy.splice(index, 1);
      setAutoTagConditions(autoTagConditionsCopy);
      return { previousAutoTagConditions: autoTagConditions };
    },
    onError: (error: Error, _, context) => {
      if (context?.previousAutoTagConditions) {
        setAutoTagConditions(context.previousAutoTagConditions);
      }
      handleMutationError(error, 'Error deleting auto tag condition', setSnackbar);
    }
  });
};

export const useUpdateAutoTagCondition = (
  jobId: number,
  autoTagConditions: TransformedAutoTagCondition[],
  setAutoTagConditions: (conditions: TransformedAutoTagCondition[]) => void,
  setSnackbar: (snackbar: SnackbarState) => void
): UseUpdateAutoTagConditionResult => {
  return useMutation<
    AutoTagCondition,
    Error,
    { conditionId: number; data: AutoTagConditionUpdateRequest },
    { previousAutoTagConditions: TransformedAutoTagCondition[] }
  >({
    mutationFn: async ({
      conditionId,
      data
    }: {
      conditionId: number;
      data: AutoTagConditionUpdateRequest;
    }) => {
      // If field_choice_id is present, convert it to field_choice_ids array
      if (data.field_choice_id !== undefined && data.field_choice_ids === undefined) {
        data.field_choice_ids = [data.field_choice_id as number];
        delete data.field_choice_id;
      }

      const response = await fetch(
        `/api/v4/jobs/${jobId}/auto_tag_conditions/${conditionId}`,
        createRequestOptions('PUT', { auto_tag_condition: data })
      );
      if (!response.ok) throw new Error('Error updating auto tag condition');
      return response.json();
    },
    onSuccess: (data, variables) => {
      if (data && variables.data.updateIndex !== undefined) {
        const tempAutoTagCondition = deepCopy(autoTagConditions);
        tempAutoTagCondition[variables.data.updateIndex] = transformCondition(data);
        setAutoTagConditions(tempAutoTagCondition);
      }
    },
    onError: (error: Error, variables, context) => {
      if (context?.previousAutoTagConditions) {
        setAutoTagConditions(context.previousAutoTagConditions);
      }
      handleMutationError(error, 'Error updating auto tag condition', setSnackbar);
    }
  });
};

/**
 * Hook for creating a new tag
 * This hook makes a POST request to the /api/v4/tags endpoint with the tag name
 * On success, it invalidates the tags query to refetch the updated list of tags
 */
export const useCreateTag = (
  setSnackbar: (snackbar: SnackbarState) => void
): UseCreateTagResult => {
  const queryClient = useQueryClient();

  return useMutation<any, Error, string, unknown>({
    mutationFn: async (tagName: string) => {
      const response = await fetch('/api/v4/tags', createRequestOptions('POST', { name: tagName }));
      if (!response.ok) throw new Error('Error creating tag');
      return response.json();
    },
    onSuccess: () => {
      // Invalidate the tags query to refetch the updated list
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      setSnackbar({ message: 'Tag created successfully', state: 'success' });
    },
    onError: (error: Error) => {
      handleMutationError(error, 'Error creating tag', setSnackbar);
    }
  });
};
